import React from "react";
import { CheckCircle2, Loader2, Thermometer } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * TestingVideoOverlay — compact overlay pinned inside the LivePlayer's video
 * container at the bottom-left, visible ONLY while status === "TESTING".
 *
 * Reads the same authoritative `testing` state that CustomerTestingPanel does,
 * so it stays in lock-step with the WS + refetch flow. It is a SIBLING of the
 * <iframe> inside LivePlayer's wrapper — updates here never cause the iframe
 * to remount because React reconciles siblings independently.
 *
 * Design constraints (from spec):
 *   - Dark translucent glass background, cyan accent border, white primary
 *     text, cyan/green completion indicators.
 *   - Compact — does not cover a large portion of the video.
 *   - Bottom-left safe zone that never overlaps the Cloudflare player controls
 *     (Cloudflare's own control bar sits at the bottom-CENTER/right).
 *   - Responsive: shrinks type + tightens spacing on iPhone widths.
 *   - Arabic/English via the existing LangProvider (useLang).
 */

const STEP_ORDER = ["windows", "gpu_driver", "bench"];
const STEP_LABEL_KEY = {
  windows: "testing_step_windows",
  gpu_driver: "testing_step_gpu_driver",
  bench: "testing_step_bench",
};

const CLASSIFICATION_KEY = {
  EXCELLENT_TEMP: "testing_class_excellent",
  GOOD_TEMP: "testing_class_good",
  HIGH_TEMP: "testing_class_high",
  TEMP_TOO_HIGH: "testing_class_too_high",
};

const SEV_COLOR = {
  success:  "#00ff66",
  normal:   "var(--accent)",
  warning:  "#ff9800",
  critical: "#ff3333",
};

function StepRow({ id, step, t }) {
  const status = step?.status || "PENDING";
  const done = status === "COMPLETED";
  const running = status === "IN_PROGRESS";
  const label = done
    ? t("testing_status_completed")
    : running
    ? (id === "bench" ? t("testing_status_testing") : t("testing_status_installing"))
    : t("testing_status_pending");
  return (
    <div
      className="flex items-center justify-between gap-3 py-1"
      data-testid={`overlay-testing-step-${id}`}
    >
      <span className="text-[10px] md:text-[11px] text-white/75 font-medium leading-tight">
        {t(STEP_LABEL_KEY[id])}
      </span>
      <span
        className="text-[10px] md:text-[11px] font-semibold flex items-center gap-1"
        style={{ color: done ? "#00ff66" : running ? "var(--accent)" : "rgba(255,255,255,0.4)" }}
        data-testid={`overlay-testing-step-${id}-status`}
      >
        {done ? <CheckCircle2 size={11} /> : running ? <Loader2 size={11} className="animate-spin" /> : null}
        {label}
      </span>
    </div>
  );
}

function TempRow({ kind, temp, t }) {
  const has = temp && temp.value !== null && temp.value !== undefined;
  const clsKey = temp?.classification && CLASSIFICATION_KEY[temp.classification];
  const color = SEV_COLOR[temp?.severity] || "rgba(255,255,255,0.6)";
  return (
    <div
      className="flex items-center justify-between gap-3 py-1"
      data-testid={`overlay-testing-temp-${kind}`}
    >
      <span className="text-[10px] md:text-[11px] text-white/75 font-medium leading-tight flex items-center gap-1">
        <Thermometer size={10} />
        {kind === "gpu" ? t("testing_temp_gpu") : t("testing_temp_cpu")}
      </span>
      {has ? (
        <span
          className="text-[10px] md:text-[11px] font-semibold flex items-center gap-1.5 whitespace-nowrap"
          style={{ color }}
          data-testid={`overlay-testing-temp-${kind}-value`}
        >
          <span className="text-white font-bold text-[11px] md:text-[12px]">{temp.value}°C</span>
          <span className="opacity-90">•</span>
          <span>{clsKey ? t(clsKey) : (temp.classification || "").replace(/_/g, " ")}</span>
        </span>
      ) : (
        <span
          className="text-[10px] md:text-[11px] italic text-white/40 font-medium"
          data-testid={`overlay-testing-temp-${kind}-waiting`}
        >
          {t("testing_waiting_result")}
        </span>
      )}
    </div>
  );
}

export function TestingVideoOverlay({ testing }) {
  const { t } = useLang();
  // Compact bottom-left overlay. `pointer-events: none` on the outer wrapper
  // means it cannot swallow taps on Cloudflare's control bar even at
  // overlapping edges — content itself has `pointer-events: auto` if needed
  // (we render no interactive controls here, so we can keep it none).
  return (
    <div
      className="absolute z-[5] pointer-events-none"
      style={{
        left: "clamp(8px, 2.5%, 16px)",
        bottom: "clamp(8px, 3%, 20px)",
        maxWidth: "min(320px, 62%)",
      }}
      data-testid="testing-video-overlay"
      dir="auto"
    >
      <div
        className="rounded-lg border px-3 py-2.5 md:px-3.5 md:py-3"
        style={{
          borderColor: "rgba(0,229,255,0.35)",
          background: "linear-gradient(180deg, rgba(6,8,12,0.78), rgba(6,8,12,0.62))",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow: "0 8px 24px -8px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.03)",
        }}
      >
        <div
          className="text-[9px] md:text-[10px] tracking-[0.22em] uppercase font-semibold mb-1.5"
          style={{ color: "var(--accent)" }}
        >
          {t("testing_title")}
        </div>
        {testing ? (
          <>
            <div className="border-t border-b py-1" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
              {STEP_ORDER.map((s) => (
                <StepRow key={s} id={s} step={testing.steps?.[s]} t={t} />
              ))}
            </div>
            <div className="pt-1">
              <TempRow kind="gpu" temp={testing.gpu_temp} t={t} />
              <TempRow kind="cpu" temp={testing.cpu_temp} t={t} />
            </div>
          </>
        ) : (
          <div className="text-[10px] md:text-[11px] text-white/50 italic">
            {t("testing_not_started")}
          </div>
        )}
      </div>
    </div>
  );
}
